import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History, Upload, RotateCcw, Loader2, GitCompare } from "lucide-react";
import { toast } from "sonner";

function DiffView({ diff }) {
  if (!diff) return null;
  return (
    <div data-testid="version-diff" className="rounded-lg border border-slate-200 overflow-hidden text-xs font-mono">
      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-slate-600 flex gap-3">
        <span>v{diff.from_version} → v{diff.to_version}</span>
        <span data-testid="diff-added" className="text-emerald-700 font-bold">+{diff.added} added</span>
        <span data-testid="diff-removed" className="text-rose-700 font-bold">−{diff.removed} removed</span>
      </div>
      <div className="max-h-[40vh] overflow-auto">
        {diff.ops.length === 0 && <div className="px-3 py-3 text-slate-500">No differences.</div>}
        {diff.ops.map((o, i) => o.op === "skip" ? (
          <div key={i} className="px-3 py-1 text-slate-400 bg-slate-50 italic">… {o.count} unchanged line(s) …</div>
        ) : (
          <div key={i} className={`px-3 py-0.5 whitespace-pre-wrap break-words ${o.op === "add" ? "bg-emerald-50 text-emerald-900" : o.op === "del" ? "bg-rose-50 text-rose-900 line-through decoration-rose-300" : "text-slate-700"}`}>
            <span className="inline-block w-4 select-none text-slate-400">{o.op === "add" ? "+" : o.op === "del" ? "−" : " "}</span>{o.text || " "}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DocumentVersions({ doc, onChanged }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [diff, setDiff] = useState(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/documents/${doc.id}/versions`).then((r) => setData(r.data)).catch(() => toast.error("Could not load versions"));
  useEffect(() => { if (open) { setDiff(null); load(); } }, [open, doc.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("change_note", note);
      const { data: d } = await api.post(`/documents/${doc.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Now at version ${d.version} — previous version archived`);
      setFile(null); setNote("");
      onChanged?.(d); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const showDiff = async (v) => {
    try { const { data: d } = await api.get(`/documents/${doc.id}/versions/${v}/diff`); setDiff(d); }
    catch { toast.error("Could not compute diff"); }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v}? The current text will be archived first.`)) return;
    try {
      const { data: d } = await api.post(`/documents/${doc.id}/versions/${v}/restore`);
      toast.success(`Restored v${v} as new version ${d.version}`);
      setDiff(null); onChanged?.(d); load();
    } catch { toast.error("Restore failed"); }
  };

  return (
    <>
      <Button data-testid={`doc-versions-btn-${doc.id}`} size="sm" variant="outline" onClick={() => setOpen(true)} className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]">
        <History className="w-4 h-4 mr-1.5" /> Versions <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#0A2A5E] text-white">v{doc.version || 1}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">Document versions — {doc.title}</DialogTitle>
            <DialogDescription>Upload a new version to replace the current text; prior versions are kept and you can compare or restore them.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-1 px-1">
            <form onSubmit={upload} data-testid="new-version-form" className="p-4 rounded-xl bg-[#F5F9FE] border border-slate-200 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <Label>New file (PDF or TXT)</Label>
                <Input data-testid="new-version-file" type="file" accept=".pdf,.txt" required onChange={(e) => setFile(e.target.files[0])} className="mt-1" />
              </div>
              <div>
                <Label>What changed?</Label>
                <Input data-testid="new-version-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 2026 revision — updated leave accrual table" className="mt-1" />
              </div>
              <Button data-testid="new-version-submit" type="submit" disabled={busy || !file} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload new version</>}
              </Button>
            </form>

            <div className="mt-4 space-y-2">
              {data && (
                <div data-testid="version-current" className="p-3 rounded-lg border border-[#0A2A5E]/30 bg-white flex flex-wrap items-center gap-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#0A2A5E] text-white">CURRENT · v{data.current.version}</span>
                  <span className="text-xs text-slate-600">{data.current.updated_at ? new Date(data.current.updated_at).toLocaleString() : ""}</span>
                  {data.current.change_note && <span className="text-xs text-slate-800 italic">“{data.current.change_note}”</span>}
                </div>
              )}
              {data?.versions.length === 0 && <div data-testid="versions-empty" className="text-sm text-slate-500 py-3">No prior versions yet — upload a new file above to start the history.</div>}
              {data?.versions.map((v) => (
                <div key={v.id} data-testid={`version-row-${v.version}`} className="p-3 rounded-lg border border-slate-200 bg-white flex flex-wrap items-center gap-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-200 text-slate-700">v{v.version}</span>
                  <div className="text-xs text-slate-600 flex-1 min-w-[10rem]">
                    Archived {new Date(v.archived_at).toLocaleString()} by {v.archived_by_name} · {v.chars.toLocaleString()} chars{v.filename ? ` · ${v.filename}` : ""}{v.reason && v.reason !== "replaced" ? ` · ${v.reason}` : ""}
                  </div>
                  <Button size="sm" variant="outline" data-testid={`version-diff-${v.version}`} onClick={() => showDiff(v.version)} className="h-8 text-xs"><GitCompare className="w-3.5 h-3.5 mr-1" /> Compare to current</Button>
                  <Button size="sm" variant="ghost" data-testid={`version-restore-${v.version}`} onClick={() => restore(v.version)} className="h-8 text-xs text-[#7C3F00]"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore</Button>
                </div>
              ))}
            </div>
            <div className="mt-4"><DiffView diff={diff} /></div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
